import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Property } from '../pages/apps/mls-portal/components/PropertyCard';

interface SearchFilters {
  searchQuery?: string;
  city?: string;
  minPrice?: string;
  maxPrice?: string;
  beds?: string;
  baths?: string;
  propertyType?: string;
  [key: string]: any;
}

interface MLSPortalState {
  properties: Property[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

interface UseMLSPortalReturn extends MLSPortalState {
  searchProperties: (filters: SearchFilters) => Promise<void>;
  getPropertyDetails: (propertyId: string) => Promise<Property | null>;
  addToFavorites: (propertyId: string) => Promise<void>;
  removeFromFavorites: (propertyId: string) => Promise<void>;
  getFavorites: () => Promise<Property[]>;
  resetSearch: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const useMLSPortal = (): UseMLSPortalReturn => {
  const [state, setState] = useState<MLSPortalState>({
    properties: [],
    loading: false,
    error: null,
    totalCount: 0,
    currentPage: 1,
    pageSize: 20
  });

  const searchProperties = useCallback(async (filters: SearchFilters) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const params = new URLSearchParams();
      
      // Add filters to params
      Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value && value !== 'any' && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      params.append('page', state.currentPage.toString());
      params.append('pageSize', state.pageSize.toString());

      const response = await axios.get(`${API_BASE_URL}/api/mls-portal/properties?${params}`);
      
      if (response.data.success) {
        setState(prev => ({
          ...prev,
          properties: response.data.properties,
          totalCount: response.data.totalCount,
          loading: false
        }));
      } else {
        throw new Error(response.data.message || 'Failed to fetch properties');
      }
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || error.message || 'Failed to search properties'
      }));
      console.error('Error searching properties:', error);
    }
  }, [state.currentPage, state.pageSize]);

  const getPropertyDetails = useCallback(async (propertyId: string): Promise<Property | null> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/mls-portal/properties/${propertyId}`);
      
      if (response.data.success) {
        return response.data.property;
      }
      return null;
    } catch (error) {
      console.error('Error fetching property details:', error);
      return null;
    }
  }, []);

  const addToFavorites = useCallback(async (propertyId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/mls-portal/favorites`,
        { propertyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error adding to favorites:', error);
      throw error;
    }
  }, []);

  const removeFromFavorites = useCallback(async (propertyId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/api/mls-portal/favorites/${propertyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error removing from favorites:', error);
      throw error;
    }
  }, []);

  const getFavorites = useCallback(async (): Promise<Property[]> => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/mls-portal/favorites`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        return response.data.favorites;
      }
      return [];
    } catch (error) {
      console.error('Error fetching favorites:', error);
      return [];
    }
  }, []);

  const resetSearch = useCallback(() => {
    setState({
      properties: [],
      loading: false,
      error: null,
      totalCount: 0,
      currentPage: 1,
      pageSize: 20
    });
  }, []);

  // Mock data for development/testing
  useEffect(() => {
    // Only use mock data if API is not available
    if (state.properties.length === 0 && !state.loading) {
      const mockProperties: Property[] = [
        {
          id: '1',
          mlsNumber: 'MLS123456',
          address: '123 Main St',
          city: 'Phoenix',
          state: 'AZ',
          zipCode: '85001',
          price: 450000,
          beds: 3,
          baths: 2,
          sqft: 2000,
          propertyType: 'Single Family',
          yearBuilt: 2015,
          listingDate: '2024-01-15',
          status: 'active',
          description: 'Beautiful single family home in a quiet neighborhood with modern amenities',
          images: ['/assets/img/property-placeholder.jpg'],
          features: ['Pool', 'Garage', 'Hardwood Floors'],
          agent: {
            name: 'John Doe',
            phone: '(555) 123-4567',
            email: 'john@example.com'
          }
        },
        {
          id: '2',
          mlsNumber: 'MLS789012',
          address: '456 Oak Ave',
          city: 'Scottsdale',
          state: 'AZ',
          zipCode: '85250',
          price: 675000,
          beds: 4,
          baths: 3,
          sqft: 2800,
          propertyType: 'Single Family',
          yearBuilt: 2018,
          listingDate: '2024-02-01',
          status: 'active',
          description: 'Luxury home with mountain views and premium finishes throughout',
          images: ['/assets/img/property-placeholder.jpg'],
          features: ['Mountain View', 'Granite Countertops', 'Walk-in Closet'],
          agent: {
            name: 'Jane Smith',
            phone: '(555) 987-6543',
            email: 'jane@example.com'
          }
        }
      ];

      // Uncomment to use mock data during development
      // setState(prev => ({ ...prev, properties: mockProperties, totalCount: 2 }));
    }
  }, [state.properties.length, state.loading]);

  return {
    ...state,
    searchProperties,
    getPropertyDetails,
    addToFavorites,
    removeFromFavorites,
    getFavorites,
    resetSearch
  };
};